import React, { useState } from 'react';

import { Combobox } from '@buildoutinc/blueprint-react/ui/Combobox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-solid-svg-icons';

interface Props {
  disabled?: boolean;
  id?: string;
  // Shown ahead of the editable tags; they can't be removed and aren't passed to onChange
  lockedTags?: string[];
  placeholder?: string;
  suggestions: string[];
  tags: string[];
  onChange: (tags: string[]) => void;
}

// Matches DocumentDefaults::TemplateTags::MAX_LENGTH
const MAX_TAG_LENGTH = 50;

const sameTag = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

export default function TagInput({
  disabled = false,
  id,
  lockedTags = [],
  placeholder = 'Add a tag',
  suggestions,
  tags,
  onChange
}: Props) {
  const [query, setQuery] = useState('');

  const value = [...lockedTags, ...tags];
  const trimmed = query.trim();
  const options = suggestions.filter(suggestion => (
    !value.some(tag => sameTag(tag, suggestion)) &&
    suggestion.toLowerCase().includes(trimmed.toLowerCase())
  ));
  const newTag = trimmed &&
    !value.some(tag => sameTag(tag, trimmed)) &&
    !options.some(option => sameTag(option, trimmed))
    ? trimmed
    : null;
  const items = newTag ? [newTag, ...options] : options;

  return (
    <Combobox
      autoHighlight
      disabled={disabled}
      filter={null}
      items={items}
      multiple
      value={value}
      onInputValueChange={setQuery}
      onValueChange={(next: string[]) => {
        onChange(next.filter(tag => !lockedTags.some(locked => sameTag(locked, tag))));
        setQuery('');
      }}
    >
      <Combobox.Chips className="form-control d-flex flex-wrap align-items-center gap-1">
        {lockedTags.map(tag => (
          <Combobox.Chip key={tag} showRemove={false} title="Set by Buildout. Can't be removed.">
            <FontAwesomeIcon className="me-1" icon={faLock} />
            {tag}
          </Combobox.Chip>
        ))}
        {tags.map(tag => (
          <Combobox.Chip aria-label={tag} key={tag} showRemove={!disabled}>
            {tag}
          </Combobox.Chip>
        ))}
        <Combobox.Input
          id={id}
          placeholder={value.length > 0 ? 'Add a tag' : placeholder}
          render={
            <input
              className="border-0 bg-transparent p-0 flex-grow-1"
              maxLength={MAX_TAG_LENGTH}
              style={{ outline: 'none', minWidth: '10rem' }}
            />
          }
        />
      </Combobox.Chips>

      <Combobox.Content>
        <Combobox.Empty>Type to add a tag</Combobox.Empty>
        <Combobox.List>
          {items.map(item => (
            <Combobox.Item key={item} value={item}>
              {item === newTag ? `Add "${item}"` : item}
            </Combobox.Item>
          ))}
        </Combobox.List>
      </Combobox.Content>
    </Combobox>
  );
}
